import posthog from 'posthog-js'

import config from 'lib/config'
import logError from 'lib/logError'
import { logger } from 'lib/utils/logger'

export const initAnalytics = () =>
  posthog.init(config.POSTHOG_API_KEY, {
    api_host: config.POSTHOG_HOST,
    defaults: '2025-05-24',
    debug: !config.IS_PROD,
    // No autocapture: only the explicit trackEvent() calls are sent, so the
    // event stream stays intentional instead of filling up with raw DOM
    // clicks/inputs. Does not affect $pageview, which is a separate option.
    autocapture: false,
    // Session replay, full fidelity — no masking (explicit product decision).
    // NOTE: replays capture the whole screen, including live customer PII
    // (chat messages, phone numbers, names, emails). Recording only actually
    // starts if "Record user sessions" is also enabled in the PostHog project
    // settings for each environment.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
      maskTextSelector: '', // record all on-screen text (no text masking)
    },
  })

export const identifyUser = (distinctId, personProperties) =>
  posthog.identify(distinctId, personProperties)

export const setAnalyticsGroup = (groupType, groupKey) =>
  groupKey && posthog.group(groupType, groupKey)

export const resetAnalytics = () => posthog.reset()

// Registers is_demo_store as a PostHog super-property so it rides on every
// event PostHog sends — manual trackEvent captures plus $pageview and session
// replay metadata. Lets demo engagement be filtered in PostHog without any
// per-event wiring. Unregisters when the active store is not a demo store.
export const setDemoStoreSuperProperty = (isDemoStore) => {
  if (!config.POSTHOG_API_KEY) {
    return
  }
  if (isDemoStore) {
    posthog.register({ is_demo_store: true })
  } else {
    posthog.unregister('is_demo_store')
  }
}

// Prefix used for the demo-store mirror events (see dispatchToProviders).
const DEMO_EVENT_PREFIX = 'demo_'
const DEMO_TOAST_TYPE = 'golden'
const DEMO_TOAST_FALLBACK_MESSAGE = 'demo event tracked'

// Debug-mode aid: surfaces a demo event as a toast with the event name in the
// `title` prop, so demo tracking can be watched live in the browser. The
// message carries the most useful payload detail available for context.
const toastDemoEvent = (eventName, payload) => {
  const detail =
    payload?.route ||
    payload?.feature ||
    payload?.card_title ||
    DEMO_TOAST_FALLBACK_MESSAGE
  window?.globals?.service?.toast(String(detail), {
    title: eventName,
    type: DEMO_TOAST_TYPE,
  })
}

// Sends a single event to every configured analytics provider. Extracted so the
// same payload can be dispatched twice: once as the real event and once as its
// demo-store mirror (demo_<eventName>) without re-running the allowlist guards.
const dispatchToProviders = (module, eventName, payload) => {
  if (config.POSTHOG_API_KEY) {
    posthog.capture(eventName, {
      ...payload,
      module,
    })
  }

  //GA4
  window.dataLayer = window?.dataLayer || []
  window.dataLayer.push({
    event: eventName,
    ...payload,
    module,
  })
}

const trackEvent = (module, eventName, payload = {}, hideToast = false) => {
  if (config.IS_TEST_MODE) {
    return
  }

  payload = {
    ...window?.globals?._logData,
    ...payload,
  }
  config.DEBUG_MODE && logger('TRACKEVENT:', { module, eventName, payload })
  // if (config.ENV === 'development') return
  const showLogs = !hideToast && config.DEBUG_MODE
  try {
    if (!Object.keys(trackEvent.modules).includes(module)) {
      showLogs &&
        window?.globals?.service?.toast(
          'TRACKEVENT: module not part of trackEvent.modules: ' + eventName,
          { type: 'error' }
        )
      return window.console.error(
        'TRACKEVENT: module not part of trackEvent.modules',
        eventName
      )
    }
    if (!Object.keys(trackEvent.events).includes(eventName)) {
      logger(
        'Object.keys(trackEvent.events)',
        Object.keys(trackEvent.events),
        eventName
      )
      showLogs &&
        window?.globals?.service?.toast(
          'TRACKEVENT: eventName not part of trackEvent.events: ' + eventName,
          { type: 'error' }
        )
      return window.console.error(
        'TRACKEVENT: eventName not part of trackEvent.eventName',
        eventName
      )
    }

    dispatchToProviders(module, eventName, payload)

    // Demo-store mirror: when the active store is a demo store, emit a
    // demo_<eventName> twin of every event so demo engagement can be tracked
    // in isolation. Gated on is_demo_store so the extra volume only applies
    // to demo stores. Skip events already prefixed so we never produce
    // demo_demo_*.
    if (payload?.is_demo_store && !eventName.startsWith(DEMO_EVENT_PREFIX)) {
      const demoEventName = `${DEMO_EVENT_PREFIX}${eventName}`
      dispatchToProviders(module, demoEventName, payload)
      showLogs && toastDemoEvent(demoEventName, payload)
    }

    if (showLogs) {
      // Demo events get the name in the toast `title`; everything else keeps
      // the plain message-only toast.
      if (eventName.startsWith(DEMO_EVENT_PREFIX)) {
        toastDemoEvent(eventName, payload)
      } else {
        window?.globals?.service?.toast(eventName)
      }
    }
  } catch (error) {
    logError(error, 'trackEvent', {
      eventName,
      module,
    })
  }
}

trackEvent.modules = {
  link_creator: 'link_creator',
  ai: 'ai',
  catalog: 'catalog',
  session_blocker: 'session_blocker',
  whatsapp_calling: 'whatsapp_calling',
  flows: 'flows',
  onboarding: 'onboarding',
  navbar: 'navbar',
  system: 'system',
  announcement: 'announcement',
  broadcast: 'broadcast',
  dock: 'dock',
  auth: 'auth',
  quickfind: 'quickfind',
  billing: 'billing',
  chat: 'chat',
  settings: 'settings',
  ctwa: 'ctwa',
  integrations: 'integrations',
  apps: 'apps',
  admin: 'admin',
  hub: 'hub',
  demo: 'demo',
}

/**
 * component_action
 * e.g: conversation_click
 **/
trackEvent.events = {
  // link_creator
  link_creator_video_transcode_failed: 'link_creator_video_transcode_failed',
  link_creator_video_transcode_start: 'link_creator_video_transcode_start',
  link_creator_video_transcode_success: 'link_creator_video_transcode_success',

  // ai
  ai_bot_language_select: 'ai_bot_language_select',
  ai_guru_suggestion_click: 'ai_guru_suggestion_click',
  ai_guru_suggestion_close_click: 'ai_guru_suggestion_close_click',
  ai_guru_suggestion_edit_click: 'ai_guru_suggestion_edit_click',
  ai_guru_suggestion_send_click: 'ai_guru_suggestion_send_click',
  ai_sello_payment_duplicate_name_found:
    'ai_sello_payment_duplicate_name_found',
  ai_sello_shipping_duplicate_name_found:
    'ai_sello_shipping_duplicate_name_found',

  // catalog
  catalog_banner_click: 'catalog_banner_click',
  catalog_payment_duplicate_name_found: 'catalog_payment_duplicate_name_found',
  catalog_publish_toggle: 'catalog_publish_toggle',
  catalog_save_click: 'catalog_save_click',
  catalog_save_next_click: 'catalog_save_next_click',

  // session_blocker
  session_blocker_continue_here_click: 'session_blocker_continue_here_click',
  session_blocker_logout_click: 'session_blocker_logout_click',
  session_blocker_show: 'session_blocker_show',

  // whatsapp_calling
  whatsapp_calling_call_ended: 'whatsapp_calling_call_ended',
  whatsapp_calling_card_shown: 'whatsapp_calling_card_shown',
  whatsapp_calling_settings_save: 'whatsapp_calling_settings_save',
  whatsapp_calling_toggle: 'whatsapp_calling_toggle',

  // flows
  flows_canvas_debug_close_click: 'flows_canvas_debug_close_click',
  flows_canvas_debug_filter_apply_click:
    'flows_canvas_debug_filter_apply_click',
  flows_canvas_debug_instance_select_click:
    'flows_canvas_debug_instance_select_click',
  flows_canvas_debug_node_open_click: 'flows_canvas_debug_node_open_click',
  flows_canvas_debug_open_click: 'flows_canvas_debug_open_click',
  flows_canvas_debug_state_filter_click:
    'flows_canvas_debug_state_filter_click',
  flows_canvas_debug_stop_click: 'flows_canvas_debug_stop_click',
  flows_canvas_footer_close_click: 'flows_canvas_footer_close_click',
  flows_canvas_footer_save_click: 'flows_canvas_footer_save_click',
  flows_canvas_free_to_paid_plan_nudge: 'flows_canvas_free_to_paid_plan_nudge',
  flows_canvas_header_back_click: 'flows_canvas_header_back_click',
  flows_canvas_header_close_click: 'flows_canvas_header_close_click',
  flows_canvas_header_publish_click: 'flows_canvas_header_publish_click',
  flows_canvas_header_use_playbook_click:
    'flows_canvas_header_use_playbook_click',
  flows_canvas_publish_no_credits: 'flows_canvas_publish_no_credits',
  flows_library_recommended_modal_close:
    'flows_library_recommended_modal_close',
  flows_library_recommended_modal_open: 'flows_library_recommended_modal_open',
  flows_library_recommended_modal_publish_click:
    'flows_library_recommended_modal_publish_click',

  // onboarding
  onboarding_apply_default_templates: 'onboarding_apply_default_templates',
  // connect_account = the self-serve /meta-connect screen (Auth/ConnectAccount).
  // Kept apart from the onboarding wizard's onboarding_mqo_meta_connect_* family
  // and from ConnectWhatsapp's onboarding_connect_* pair, so each screen's
  // funnel stands on its own.
  onboarding_connect_account_callback_failed:
    'onboarding_connect_account_callback_failed',
  onboarding_connect_account_callback_success:
    'onboarding_connect_account_callback_success',
  onboarding_connect_account_demo_back_click:
    'onboarding_connect_account_demo_back_click',
  onboarding_connect_account_facebook_click:
    'onboarding_connect_account_facebook_click',
  onboarding_connect_account_facebook_error:
    'onboarding_connect_account_facebook_error',
  onboarding_connect_account_help_click:
    'onboarding_connect_account_help_click',
  onboarding_connect_account_logout_click:
    'onboarding_connect_account_logout_click',
  onboarding_connect_account_qr_click: 'onboarding_connect_account_qr_click',
  onboarding_connect_account_refresh_click:
    'onboarding_connect_account_refresh_click',
  onboarding_connect_account_visit: 'onboarding_connect_account_visit',
  onboarding_connect_success: 'onboarding_connect_success',
  onboarding_connect_whatsapp: 'onboarding_connect_whatsapp',
  onboarding_create_account_click: 'onboarding_create_account_click',
  onboarding_demo_store_create_error: 'onboarding_demo_store_create_error',
  onboarding_demo_store_create_success: 'onboarding_demo_store_create_success',
  onboarding_meta_connect_success: 'onboarding_meta_connect_success',
  onboarding_meta_connect_visit: 'onboarding_meta_connect_visit',
  onboarding_mqo_business_phone_duplicate_error:
    'onboarding_mqo_business_phone_duplicate_error',
  onboarding_mqo_business_phone_duplicate_error_close_click:
    'onboarding_mqo_business_phone_duplicate_error_close_click',
  onboarding_mqo_business_phone_duplicate_error_contact_us_click:
    'onboarding_mqo_business_phone_duplicate_error_contact_us_click',
  onboarding_mqo_business_phone_duplicate_error_go_back_to_login_click:
    'onboarding_mqo_business_phone_duplicate_error_go_back_to_login_click',
  onboarding_mqo_delete_whatsapp_account_click:
    'onboarding_mqo_delete_whatsapp_account_click',
  onboarding_mqo_disable_2fa_for_whatsapp_api_click:
    'onboarding_mqo_disable_2fa_for_whatsapp_api_click',
  onboarding_mqo_fb_object_setup: 'onboarding_mqo_fb_object_setup',
  onboarding_mqo_free_number_request: 'onboarding_mqo_free_number_request',
  onboarding_mqo_lead: 'onboarding_mqo_lead',
  onboarding_mqo_meta_connect_cloud_error:
    'onboarding_mqo_meta_connect_cloud_error',
  onboarding_mqo_meta_connect_cloud_request:
    'onboarding_mqo_meta_connect_cloud_request',
  onboarding_mqo_meta_connect_cloud_success:
    'onboarding_mqo_meta_connect_cloud_success',
  onboarding_mqo_meta_connect_error: 'onboarding_mqo_meta_connect_error',
  onboarding_mqo_meta_connect_request: 'onboarding_mqo_meta_connect_request',
  onboarding_mqo_meta_connect_success: 'onboarding_mqo_meta_connect_success',
  onboarding_mqo_meta_connect_support_widget_click:
    'onboarding_mqo_meta_connect_support_widget_click',
  onboarding_mqo_meta_payment_status_modal_load:
    'onboarding_mqo_meta_payment_status_modal_load',
  onboarding_mqo_migration_select: 'onboarding_mqo_migration_select',
  onboarding_mqo_migration_visit: 'onboarding_mqo_migration_visit',
  onboarding_mqo_otp_resend_error: 'onboarding_mqo_otp_resend_error',
  onboarding_mqo_otp_resend_request: 'onboarding_mqo_otp_resend_request',
  onboarding_mqo_otp_resend_success: 'onboarding_mqo_otp_resend_success',
  onboarding_mqo_otp_verification_error:
    'onboarding_mqo_otp_verification_error',
  onboarding_mqo_otp_verification_request:
    'onboarding_mqo_otp_verification_request',
  onboarding_mqo_otp_verification_success:
    'onboarding_mqo_otp_verification_success',
  onboarding_mqo_request_verification_error:
    'onboarding_mqo_request_verification_error',
  onboarding_mqo_request_verification_request:
    'onboarding_mqo_request_verification_request',
  onboarding_mqo_request_verification_success:
    'onboarding_mqo_request_verification_success',
  onboarding_mqo_save_lead_error: 'onboarding_mqo_save_lead_error',
  onboarding_mqo_session_log_step: 'onboarding_mqo_session_log_step',
  onboarding_mqo_session_logs: 'onboarding_mqo_session_logs',
  onboarding_mqo_website_field_load: 'onboarding_mqo_website_field_load',
  onboarding_mqo_website_verification_request:
    'onboarding_mqo_website_verification_request',
  onboarding_mqo_whatsapp_display_name_guidelines_click:
    'onboarding_mqo_whatsapp_display_name_guidelines_click',
  onboarding_mqo_whatsapp_display_name_validation_request:
    'onboarding_mqo_whatsapp_display_name_validation_request',
  onboarding_qr_code_accepted: 'onboarding_qr_code_accepted',
  onboarding_qr_shown: 'onboarding_qr_shown',
  onboarding_shopify_contact_phone_input:
    'onboarding_shopify_contact_phone_input',
  onboarding_shopify_welcome_contact_no_back_click:
    'onboarding_shopify_welcome_contact_no_back_click',
  onboarding_shopify_welcome_contact_submit_back_click:
    'onboarding_shopify_welcome_contact_submit_back_click',
  onboarding_shopify_welcome_login_click:
    'onboarding_shopify_welcome_login_click',
  onboarding_shopify_welcome_continue_click:
    'onboarding_shopify_welcome_continue_click',
  onboarding_shopify_welcome_prompt_show:
    'onboarding_shopify_welcome_prompt_show',
  onboarding_shopify_welcome_prompt_widget_click:
    'onboarding_shopify_welcome_prompt_widget_click',
  onboarding_shopify_welcome_show: 'onboarding_shopify_welcome_show',
  onboarding_shopify_welcome_unable_to_scan_click:
    'onboarding_shopify_welcome_unable_to_scan_click',
  onboarding_shopify_welcome_unable_to_scan_submitted:
    'onboarding_shopify_welcome_unable_to_scan_submitted',
  onboarding_signup_click: 'onboarding_signup_click',
  onboarding_whatsapp_blocker_click: 'onboarding_whatsapp_blocker_click',
  onboarding_whatsapp_connect_api_call: 'onboarding_whatsapp_connect_api_call',
  onboarding_whatsapp_connect_callback_received:
    'onboarding_whatsapp_connect_callback_received',
  onboarding_whatsapp_connect_success_page_call:
    'onboarding_whatsapp_connect_success_page_call',

  // navbar
  navbar_account_settings_click: 'navbar_account_settings_click',
  navbar_add_inbox_click: 'navbar_add_inbox_click',
  navbar_add_inbox_folder_click: 'navbar_add_inbox_folder_click',
  navbar_agent_analytics_click: 'navbar_agent_analytics_click',
  navbar_ai_automation_click: 'navbar_ai_automation_click',
  navbar_analytics_click: 'navbar_analytics_click',
  navbar_change_agent_click: 'navbar_change_agent_click',
  navbar_create_new_org_click: 'navbar_create_new_org_click',
  navbar_store_switch: 'navbar_store_switch',

  // system
  system_app_crash: 'system_app_crash',
  system_conversation_subscription_missing:
    'system_conversation_subscription_missing',
  system_conversations_same_timestamp: 'system_conversations_same_timestamp',
  system_file_upload_failed: 'system_file_upload_failed',
  system_health_banner_show: 'system_health_banner_show',
  system_internet_offline_toast: 'system_internet_offline_toast',
  system_pinned_chats_overflow: 'system_pinned_chats_overflow',
  system_realtime_invalid_topic_subscription:
    'system_realtime_invalid_topic_subscription',
  system_realtime_subscription_missed: 'system_realtime_subscription_missed',
  system_reload_lazy_click: 'system_reload_lazy_click',

  // announcement
  announcement_accept: 'announcement_accept',
  announcement_contact_support: 'announcement_contact_support',
  announcement_dismiss: 'announcement_dismiss',
  announcement_learn_more: 'announcement_learn_more',
  announcement_rolling_popup_close: 'announcement_rolling_popup_close',
  announcement_rolling_popup_cta: 'announcement_rolling_popup_cta',
  announcement_rolling_popup_show: 'announcement_rolling_popup_show',
  announcement_show: 'announcement_show',

  // broadcast
  broadcast_create_add_contacts_click: 'broadcast_create_add_contacts_click',
  broadcast_create_advanced_import_toggle:
    'broadcast_create_advanced_import_toggle',
  broadcast_create_back_click: 'broadcast_create_back_click',
  broadcast_create_breadcrumb_click: 'broadcast_create_breadcrumb_click',
  broadcast_create_cancel_click: 'broadcast_create_cancel_click',
  broadcast_create_conversion_tracking_toggle:
    'broadcast_create_conversion_tracking_toggle',
  broadcast_create_next_click: 'broadcast_create_next_click',
  broadcast_create_reattempts_toggle: 'broadcast_create_reattempts_toggle',
  broadcast_create_save_as_draft_click: 'broadcast_create_save_as_draft_click',
  broadcast_create_schedule_select: 'broadcast_create_schedule_select',
  broadcast_create_send_failed: 'broadcast_create_send_failed',
  broadcast_create_send_success: 'broadcast_create_send_success',
  broadcast_create_sheet_add_row: 'broadcast_create_sheet_add_row',
  broadcast_create_sheet_go_to_row: 'broadcast_create_sheet_go_to_row',
  broadcast_create_template_select: 'broadcast_create_template_select',
  broadcast_create_type_select: 'broadcast_create_type_select',

  // dock
  dock_bottom_expand_click: 'dock_bottom_expand_click',
  dock_customer_profile_edit_name: 'dock_customer_profile_edit_name',
  dock_customer_profile_edit_name_cancel:
    'dock_customer_profile_edit_name_cancel',
  dock_customer_profile_edit_name_save: 'dock_customer_profile_edit_name_save',
  dock_customer_profile_media_click: 'dock_customer_profile_media_click',
  dock_customer_profile_media_tabs_click:
    'dock_customer_profile_media_tabs_click',
  dock_customer_profile_sections_click: 'dock_customer_profile_sections_click',
  dock_orders_action_click: 'dock_orders_action_click',
  dock_orders_card_toggle: 'dock_orders_card_toggle',
  dock_orders_search: 'dock_orders_search',
  dock_orders_tab_change: 'dock_orders_tab_change',
  dock_tab_click: 'dock_tab_click',
  dock_tags_delete_tag: 'dock_tags_delete_tag',

  // auth
  auth_2fa_setup_success: 'auth_2fa_setup_success',
  auth_autologin_success: 'auth_autologin_success',
  auth_create_account_visit: 'auth_create_account_visit',
  auth_log_out: 'auth_log_out',
  auth_login_flow: 'auth_login_flow',
  auth_login_mode_switch: 'auth_login_mode_switch',
  auth_login_security: 'auth_login_security',
  auth_login_visit: 'auth_login_visit',
  auth_mfa_enroll_error: 'auth_mfa_enroll_error',
  auth_mfa_enroll_request_otp: 'auth_mfa_enroll_request_otp',
  auth_mfa_login_error: 'auth_mfa_login_error',
  auth_mfa_login_request_otp: 'auth_mfa_login_request_otp',
  auth_recaptcha_skipped: 'auth_recaptcha_skipped',
  auth_recaptcha_verified: 'auth_recaptcha_verified',
  auth_recaptcha_blocked: 'auth_recaptcha_blocked',
  auth_recaptcha_error: 'auth_recaptcha_error',
  auth_signup_flow: 'auth_signup_flow',
  auth_signup_lead: 'auth_signup_lead',
  auth_store_switch_no_access: 'auth_store_switch_no_access',
  auth_track_reditus: 'auth_track_reditus',
  auth_verify_phone_submit: 'auth_verify_phone_submit',

  // quickfind
  quickfind_clear_click: 'quickfind_clear_click',
  quickfind_hide_closed_chats: 'quickfind_hide_closed_chats',
  quickfind_inbox_created: 'quickfind_inbox_created',

  // billing
  billing_facebook_setup_click: 'billing_facebook_setup_click',
  billing_phone_pe_checkout_cancelled: 'billing_phone_pe_checkout_cancelled',
  billing_phone_pe_checkout_concluded: 'billing_phone_pe_checkout_concluded',
  billing_phone_pe_checkout_initiated: 'billing_phone_pe_checkout_initiated',
  billing_shopify_currency_mismatch: 'billing_shopify_currency_mismatch',
  billing_shopify_migration_agreement_click:
    'billing_shopify_migration_agreement_click',
  billing_shopify_migration_credits_migrate_failure:
    'billing_shopify_migration_credits_migrate_failure',
  billing_shopify_migration_credits_migrate_success:
    'billing_shopify_migration_credits_migrate_success',
  billing_shopify_migration_reset_failure:
    'billing_shopify_migration_reset_failure',
  billing_shopify_migration_reset_success:
    'billing_shopify_migration_reset_success',
  billing_shopify_migration_warning_modal_shown:
    'billing_shopify_migration_warning_modal_shown',
  billing_stripe_checkout_initiated: 'billing_stripe_checkout_initiated',
  billing_stripe_success_page_reached: 'billing_stripe_success_page_reached',
  billing_update_subscription_plan: 'billing_update_subscription_plan',
  billing_upgrade_click: 'billing_upgrade_click',

  // chat
  chat_conversation_list_retry_click: 'chat_conversation_list_retry_click',
  chat_instagram_reel_link_click: 'chat_instagram_reel_link_click',
  chat_instagram_reel_preview_click: 'chat_instagram_reel_preview_click',
  chat_instagram_reply_via_dm: 'chat_instagram_reply_via_dm',
  chat_media_sent_unconverted: 'chat_media_sent_unconverted',
  chat_messages_retry_click: 'chat_messages_retry_click',
  chat_mobile_app_banner_accept: 'chat_mobile_app_banner_accept',
  chat_mobile_app_banner_dismiss: 'chat_mobile_app_banner_dismiss',
  chat_mobile_app_banner_show: 'chat_mobile_app_banner_show',
  chat_send_message_failure: 'chat_send_message_failure',
  chat_window_conversation_list_refresh_click:
    'chat_window_conversation_list_refresh_click',
  chat_window_enigma_takeover_click: 'chat_window_enigma_takeover_click',
  chat_window_file_upload_attempt: 'chat_window_file_upload_attempt',
  chat_window_file_upload_success: 'chat_window_file_upload_success',
  chat_window_message_delivery_failed: 'chat_window_message_delivery_failed',
  chat_window_message_send_failed: 'chat_window_message_send_failed',

  // settings
  settings_agent_self_delete_blocked: 'settings_agent_self_delete_blocked',
  settings_contacts_export: 'settings_contacts_export',
  settings_tyntec_migration_banner_show:
    'settings_tyntec_migration_banner_show',
  settings_tyntec_migration_cta_click: 'settings_tyntec_migration_cta_click',

  // ctwa
  ctwa_list_open: 'ctwa_list_open',
  ctwa_overview_data_tab_open: 'ctwa_overview_data_tab_open',
  ctwa_overview_export_click: 'ctwa_overview_export_click',
  ctwa_overview_open: 'ctwa_overview_open',

  // integrations
  integrations_card_connect_click: 'integrations_card_connect_click',
  integrations_card_delete_click: 'integrations_card_delete_click',
  integrations_modal_connect_click: 'integrations_modal_connect_click',
  integrations_shopify_merger: 'integrations_shopify_merger',
  integrations_shopify_merger_mismatch: 'integrations_shopify_merger_mismatch',
  integrations_whatsapp_pay_cancel: 'integrations_whatsapp_pay_cancel',
  integrations_whatsapp_pay_connect: 'integrations_whatsapp_pay_connect',

  // apps
  apps_card_click: 'apps_card_click',

  // admin
  admin_disable_bulk_2fa: 'admin_disable_bulk_2fa',

  // hub
  hub_date_range_change: 'hub_date_range_change',
  hub_revenue_table_tab_change: 'hub_revenue_table_tab_change',
  hub_sidebar_click: 'hub_sidebar_click',
  hub_suggestion_click: 'hub_suggestion_click',

  // demo — only fired when the current store is a demo store (is_demo === true),
  // to keep event volume limited to demo engagement tracking.
  demo_add_store_click: 'demo_add_store_click',
  demo_banner_connect_click: 'demo_banner_connect_click',
  demo_banner_show: 'demo_banner_show',
  demo_conversation_open: 'demo_conversation_open',
  demo_feature_open: 'demo_feature_open',
  demo_landing_view: 'demo_landing_view',
  demo_message_send: 'demo_message_send',
  demo_quick_reply_click: 'demo_quick_reply_click',
}

export default trackEvent
